




import Vue from "vue";
import * as d3 from "d3";

export default Vue.extend({
  name: "SkillsAnimation",
  components: {},
  data() {
    return {
      local_data: [] as any
    };
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    width: {
      type: Number,
      default: 1500
    },
    height: {
      type: Number,
      default: 700
    }
  },
  mounted() {
    this.local_data = this.data;
    this.createBubbleChart(this.data, this.$refs.rightChart);
  },
  watch: {
    data(oldData: any, newData: any) {
      this.local_data = newData;
      this.createBubbleChart(this.data, this.$refs.rightChart);
    }
  },
  methods: {
    createBubbleChart(d: any, ref: any) {
      if (!Array.isArray(d)) {
        console.error("Expected data to be an array", d);
        return;
      }
      const data = d;
      const width = this.width;
      const height = this.height;

      // Clear the previous SVG elements
      d3.select(ref).select("svg").remove();

      const svg = d3
        .select(ref)
        .append("svg")
        .attr("width", "100%")
        .attr("height", height)
        .attr("viewBox", `0 0 ${width} ${height}`)
        .attr("preserveAspectRatio", "xMidYMid meet");

      // Define the gradient in the defs section
      const defs = svg.append("defs");
      const gradient = defs
        .append("linearGradient")
        .attr("id", "circleGradient")
        .attr("x1", "0%")
        .attr("y1", "0%")
        .attr("x2", "100%")
        .attr("y2", "0%");

      gradient
        .append("stop")
        .attr("offset", "0%")
        .attr("stop-color", "rgba(255, 255, 255, 0.05)");

      gradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "rgba(229, 229, 229, 0.05)");

      const maxRadius = 65;
      const rScale = d3
        .scaleSqrt()
        .domain([0, d3.max(data, (d: any) => d.size)])
        .range([5, maxRadius]);

      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "tooltip")
        .style("position", "absolute")
        .style("z-index", "9000")
        .style("top", "0")
        .style("background", "#01997c")
        .style("opacity", 0)
        .style("color", "#fff")
        .style("padding", "5px 10px")
        .style("border-radius", "12px")
        .style("border", "1px solid rgba(255, 255, 255, 0.2)");

      const boundaryForce = () => {
        return (alpha: any) => {
          data.forEach((d: any) => {
            const halfSquareSize = 60;
            if (d.x < halfSquareSize) d.x = halfSquareSize; // Left boundary
            if (d.x > width - halfSquareSize) d.x = width - halfSquareSize; // Right boundary
            if (d.y < halfSquareSize) d.y = halfSquareSize; // Top boundary
            if (d.y > height - halfSquareSize) d.y = height - halfSquareSize; // Bottom boundary
          });
        };
      };

      const simulation = d3
        .forceSimulation(data)
        .force("x", d3.forceX(width / 2).strength(0.06))
        .force("y", d3.forceY(height / 2).strength(0.0))
        .force(
          "collide",
          d3.forceCollide((d: any) => rScale(d.size) + 6)
        )
        .force("boundary", boundaryForce())
        .on("tick", ticked);

      function ticked() {
        const circles = svg
          .selectAll("g")
          .data(data)
          .join(
            (enter: any) => {
              const g = enter.append("g");

              g.append("circle")
                .attr("r", (d: any) => rScale(d.size))
                .attr("fill", "url(#circleGradient)") // Apply the gradient to the circle
                .style("cursor", "pointer")
                .style("position", "relative")
                .style("z-index", 13000)
                .on(
                  "mouseover",
                  function (this: SVGRectElement, event: MouseEvent, d: any) {
                    d3.select(this)
                      .attr("stroke", "#fff")
                      .attr("stroke-width", 2)
                      .style("z-index", 132000);
                    tooltip
                      .transition()
                      .duration(200)
                      .style("opacity", 0.9)
                      .style("z-index", 6000)
                      .style("left", `${event.pageX + 10}px`)
                      .style("top", `${event.pageY - 28}px`);
                    tooltip.html(
                      `<strong>Skill:</strong> ${d.skill}<br><strong>Group:</strong> ${d.group}`
                    );
                  }
                )
                .on("mouseout", function (this: SVGRectElement) {
                  d3.select(this).attr("stroke", null);
                  tooltip.transition().duration(500).style("opacity", 0);
                });

              g.append("text")
                .attr("text-anchor", "middle")
                .attr("dy", ".35em")
                .text((d: any) => {
                  const maxLength = d.size < 35 ? (d.size < 12 ? 5 : 9) : 15; // Adjust based on size
                  return truncateText(d.skill, maxLength);
                })
                .style("fill", "#fff")
                .style("font-size", "12px")
                .style("z-index", 6000);

              return g;
            },
            (update: any) => update,
            (exit: any) => exit.remove()
          )
          .attr("transform", (d: any) => `translate(${d.x},${d.y})`);
      }

      function truncateText(skill: string, maxLength: number) {
        return skill.length > maxLength
          ? `${skill.slice(0, maxLength)}...`
          : skill;
      }
    }
  }
});
